import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { beamSolid, boxSolid, horizontalCap, prismSolid, subFrame } from '../solids';
import { gableRoof } from '../roofSolids';
import { corners, type PlanRect } from './genericBlock';
import { gableWall, rectWall, type MasonryStyles } from './masonry';
import { dominantAxis, meshFrameOf, planBounds, planFrameOf } from './planFrame';
import { buildNieuweBrugWestTower } from './nieuweBrugWestTower';

export const NIEUWE_BRUG_BUILDINGS = new Set([
  'landmark-nieuwe-brug-west-tower',
  'landmark-nieuwe-brug-blockhouse',
  'landmark-nieuwe-brug-east-headhouse',
]);

export interface EntranceStyles {
  readonly masonry: MasonryStyles;
  readonly timber: VertexStyle;
  readonly tile: VertexStyle;
}

function window(x: number, y: number, width: number, height: number): Ring {
  return [
    [x - width / 2, y],
    [x + width / 2, y],
    [x + width / 2, y + height],
    [x - width / 2, y + height],
  ];
}

/** Individually measured rows, rather than the ordinary-house window generator. */
function walls(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  r: PlanRect,
  foot: number,
  eaves: number,
  rows: readonly number[],
  doorSide: number | null,
  gables?: { ridge: number; alongU: boolean }
): void {
  const c = corners(r);
  c.forEach((a, i) => {
    const b = c[(i + 1) % c.length]!;
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const count = Math.max(1, Math.floor(length / 3.3));
    const openings = rows.flatMap((y) =>
      Array.from({ length: count }, (_, k) => window(((k + 0.5) * length) / count, y, 0.65, 1.2))
    );
    if (i === doorSide) openings.push(window(length / 2, foot + 0.03, 1.2, 2.2));
    const end = gables && (gables.alongU ? i % 2 === 1 : i % 2 === 0);
    if (end) {
      openings.push(window(length / 2, eaves + 0.8, 0.55, 0.8));
      // Translate a gable's bottom into its own frame so elevated timber rooms keep their floor.
      const raised = subFrame(frame, 0, 0, 0, foot);
      const shifted = openings.map((ring) => ring.map((p): Vec2 => [p[0], p[1] - foot]));
      gableWall(
        builder,
        styles,
        raised,
        [a, b],
        { eaveY: eaves - foot, apexY: gables.ridge - foot },
        shifted
      );
    } else rectWall(builder, styles, frame, [a, b], [foot, eaves], openings, 0);
  });
  horizontalCap(builder, styles.wall, frame, c, foot, false);
}

function eaveBand(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  r: PlanRect,
  y: number
): void {
  // A stepped cornice has a physical shadow at walking height, rather than a painted-on line.
  const outer = { u0: r.u0 - 0.12, u1: r.u1 + 0.12, v0: r.v0 - 0.12, v1: r.v1 + 0.12 };
  prismSolid(builder, style, frame, corners(outer), y - 0.18, y, { top: true, bottom: true });
}

function timberJoint(
  builder: MeshBuilder,
  styles: EntranceStyles,
  frame: Frame,
  r: PlanRect,
  elevation: number
): void {
  const floor = 4.1,
    eaves = 13.0;
  const masonry = { ...styles.masonry, wall: styles.timber, glazing: { reveal: styles.timber } };
  const ridge = eaves + (r.v1 - r.v0) * 0.65;
  walls(builder, masonry, frame, r, floor, eaves, [6.1, 9.5], 0, { ridge, alongU: true });
  gableRoof(builder, masonry, frame, {
    ...r,
    eaveY: eaves,
    ridgeY: ridge,
    ends: { start: false, end: false },
  });
  // Six submerged posts, cross caps and diagonal stays carry the block beside the clear walking lane.
  const us = [r.u0 + 0.3, 0, r.u1 - 0.3],
    vs = [r.v0 + 0.3, r.v1 - 0.3];
  for (const u of us) {
    for (const v of vs)
      beamSolid(builder, styles.timber, frame, [u, -2.5 - elevation, -v], [u, floor, -v], 0.18);
    beamSolid(
      builder,
      styles.timber,
      frame,
      [u, floor - 0.2, -r.v0],
      [u, floor - 0.2, -r.v1],
      0.16
    );
    beamSolid(builder, styles.timber, frame, [u, -0.3, -vs[0]!], [u, floor - 0.4, -vs[1]!], 0.11);
  }
  for (const v of vs) {
    beamSolid(
      builder,
      styles.timber,
      frame,
      [r.u0, floor - 0.2, -v],
      [r.u1, floor - 0.2, -v],
      0.16
    );
    beamSolid(builder, styles.timber, frame, [r.u0, 0, -v], [r.u1, floor - 0.5, -v], 0.1);
  }
  // Exposed sill, corner posts and rails distinguish the boarded building from masonry.
  for (const u of [r.u0 - 0.04, r.u1 + 0.04])
    for (const v of [r.v0 + 0.04, r.v1 - 0.04])
      beamSolid(builder, styles.timber, frame, [u, floor, -v], [u, eaves, -v], 0.09);
  for (const v of [r.v0 - 0.07, r.v1 + 0.07])
    for (const y of [floor + 0.1, 8.8, eaves - 0.1])
      beamSolid(builder, styles.timber, frame, [r.u0, y, -v], [r.u1, y, -v], 0.08);
}

function eastHead(builder: MeshBuilder, styles: EntranceStyles, frame: Frame, r: PlanRect): void {
  const masonry = {
    ...styles.masonry,
    roof: styles.tile,
    glazing: { reveal: styles.masonry.wall },
  };
  const split = r.v0 + (r.v1 - r.v0) * 0.65;
  const main = { ...r, v1: split },
    wing = { ...r, v0: split + 0.015 };
  for (const [rect, eaves] of [
    [main, 11],
    [wing, 7.6],
  ] as const) {
    const ridge = eaves + (rect.v1 - rect.v0) * 0.7;
    walls(builder, masonry, frame, rect, -0.1, eaves, eaves > 10 ? [2.8, 6.5] : [3.1], 1, {
      ridge,
      alongU: true,
    });
    gableRoof(builder, masonry, frame, {
      ...rect,
      eaveY: eaves,
      ridgeY: ridge,
      ends: { start: false, end: false },
    });
    eaveBand(builder, masonry.wall, frame, rect, eaves);
  }
  boxSolid(
    builder,
    masonry.wall,
    frame,
    [r.u0 + 2, 11, -split + 1.3],
    [r.u0 + 2.8, 15.5, -split + 2.1],
    { top: true, bottom: true }
  );
  boxSolid(
    builder,
    masonry.wall,
    frame,
    [r.u0 + 1.9, 15.5, -split + 1.2],
    [r.u0 + 2.9, 15.8, -split + 2.2],
    { top: true, bottom: true }
  );
}

/** The painted entrance buildings each retain their own plan, materials and supporting structure. */
export function buildNieuweBrugBuilding(
  builder: MeshBuilder,
  landmark: Landmark,
  styles: EntranceStyles,
  elevation = 0
): void {
  const plan = planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
  const frame = meshFrameOf(plan),
    rect = planBounds(plan);
  if (landmark.id === 'landmark-nieuwe-brug-west-tower')
    buildNieuweBrugWestTower(builder, styles, frame, elevation);
  else if (landmark.id === 'landmark-nieuwe-brug-blockhouse')
    timberJoint(builder, styles, frame, rect, elevation);
  else eastHead(builder, styles, frame, rect);
}
